import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InvoicePreviewModal from './components/InvoicePreviewModal';
import EmailDeliveryModal from './components/EmailDeliveryModal';

const InvoiceManagementHub = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('last30days');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [previewModal, setPreviewModal] = useState({ isOpen: false, invoice: null });
  const [emailModal, setEmailModal] = useState({ isOpen: false, invoice: null });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [invoices, setInvoices] = useState([
    {
      id: 1,
      invoiceNumber: 'INV-2026-001',
      customerName: 'Acme Corp',
      customerEmail: 'contact@acmecorp.com',
      customerAddress: '123 Business St, Mumbai, MH 400001',
      amount: 14999,
      taxAmount: 2700,
      totalAmount: 17699,
      issueDate: '2026-02-01',
      dueDate: '2026-02-15',
      status: 'Paid',
      paidDate: '2026-02-05',
      paymentMethod: 'Credit Card',
      items: [
        { description: 'Enterprise Plan - Monthly', quantity: 1, unitPrice: 14999, amount: 14999 }
      ],
      notes: 'Thank you for your business',
      paymentTerms: 'Payment due within 14 days',
      sentDate: '2026-02-01',
      viewedDate: '2026-02-02'
    },
    {
      id: 2,
      invoiceNumber: 'INV-2026-002',
      customerName: 'Tech Solutions Ltd',
      customerEmail: 'admin@techsolutions.com',
      customerAddress: '456 Tech Park, Bangalore, KA 560001',
      amount: 5999,
      taxAmount: 1080,
      totalAmount: 7079,
      issueDate: '2026-02-03',
      dueDate: '2026-02-17',
      status: 'Sent',
      paidDate: null,
      paymentMethod: null,
      items: [
        { description: 'Scale Plan - Monthly', quantity: 1, unitPrice: 5999, amount: 5999 }
      ],
      notes: 'Thank you for your business',
      paymentTerms: 'Payment due within 14 days',
      sentDate: '2026-02-03',
      viewedDate: '2026-02-04'
    },
    {
      id: 3,
      invoiceNumber: 'INV-2026-003',
      customerName: 'StartupXYZ',
      customerEmail: 'founder@startupxyz.com',
      customerAddress: '789 Startup Hub, Pune, MH 411001',
      amount: 2999,
      taxAmount: 540,
      totalAmount: 3539,
      issueDate: '2026-02-05',
      dueDate: '2026-02-19',
      status: 'Overdue',
      paidDate: null,
      paymentMethod: null,
      items: [
        { description: 'Growth Plan - Monthly', quantity: 1, unitPrice: 2999, amount: 2999 }
      ],
      notes: 'Thank you for your business',
      paymentTerms: 'Payment due within 14 days',
      sentDate: '2026-02-05',
      viewedDate: '2026-02-06'
    },
    {
      id: 4,
      invoiceNumber: 'INV-2026-004',
      customerName: 'Digital Ventures',
      customerEmail: 'billing@digitalventures.com',
      customerAddress: '321 Digital Ave, Delhi, DL 110001',
      amount: 2999,
      taxAmount: 540,
      totalAmount: 3539,
      issueDate: '2026-02-07',
      dueDate: '2026-02-21',
      status: 'Draft',
      paidDate: null,
      paymentMethod: null,
      items: [
        { description: 'Growth Plan - Monthly', quantity: 1, unitPrice: 2999, amount: 2999 }
      ],
      notes: 'Thank you for your business',
      paymentTerms: 'Payment due within 14 days',
      sentDate: null,
      viewedDate: null
    },
    {
      id: 5,
      invoiceNumber: 'INV-2026-005',
      customerName: 'Global Enterprises',
      customerEmail: 'finance@globalent.com',
      customerAddress: '555 Corporate Blvd, Hyderabad, TS 500001',
      amount: 14999,
      taxAmount: 2700,
      totalAmount: 17699,
      issueDate: '2026-02-08',
      dueDate: '2026-02-22',
      status: 'Viewed',
      paidDate: null,
      paymentMethod: null,
      items: [
        { description: 'Enterprise Plan - Monthly', quantity: 1, unitPrice: 14999, amount: 14999 }
      ],
      notes: 'Thank you for your business',
      paymentTerms: 'Payment due within 14 days',
      sentDate: '2026-02-08',
      viewedDate: '2026-02-08'
    }
  ]);

  const filteredInvoices = invoices?.filter(invoice => {
    const matchesSearch = invoice?.customerName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         invoice?.invoiceNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         invoice?.customerEmail?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice?.status === statusFilter;
    const matchesCustomer = customerFilter === 'all' || invoice?.customerName === customerFilter;
    return matchesSearch && matchesStatus && matchesCustomer;
  });

  const totalPages = Math.ceil(filteredInvoices?.length / itemsPerPage);
  const paginatedInvoices = filteredInvoices?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleDownloadPDF = (invoice) => {
    // Simulate PDF download
    alert(`Downloading PDF for ${invoice?.invoiceNumber}`);
  };

  const handleSendEmail = (invoice) => {
    setEmailModal({ isOpen: true, invoice });
  };

  const confirmEmailSend = (emailData) => {
    setInvoices(prev => prev?.map(inv => {
      if (inv?.id === emailData?.invoiceId) {
        return {
          ...inv,
          status: inv?.status === 'Draft' ? 'Sent' : inv?.status,
          sentDate: inv?.sentDate || new Date()?.toISOString()?.split('T')?.[0]
        };
      }
      return inv;
    }));
    setEmailModal({ isOpen: false, invoice: null });
    alert('Invoice sent successfully!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      Paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      Sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      Viewed: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      Overdue: 'bg-red-500/20 text-red-400 border-red-500/30',
      Draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    return styles?.[status] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  const stats = {
    total: invoices?.length,
    paid: invoices?.filter(i => i?.status === 'Paid')?.length,
    overdue: invoices?.filter(i => i?.status === 'Overdue')?.length,
    draft: invoices?.filter(i => i?.status === 'Draft')?.length,
    totalRevenue: invoices?.filter(i => i?.status === 'Paid')?.reduce((sum, i) => sum + i?.totalAmount, 0)
  };

  const uniqueCustomers = [...new Set(invoices.map(i => i.customerName))];

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
      }`}>
        {/* Header */}
        <header className="bg-slate-900 border-b border-slate-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white font-heading">
                Invoice Management Hub
              </h1>
              <p className="text-sm text-slate-400 mt-1 font-body">
                Generate, manage, and deliver professional invoices
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button className="bg-amber-500 hover:bg-amber-600">
                <Icon name="Plus" size={18} />
                <span className="ml-2">Create Invoice</span>
              </Button>
              <Button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden lg:flex"
                variant="outline"
              >
                <Icon name={sidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </Button>
            </div>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="px-6 py-4 bg-slate-900/50">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Total Invoices</p>
                  <p className="text-2xl font-bold text-white mt-1 font-heading">{stats?.total}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center">
                  <Icon name="FileText" size={24} color="#94A3B8" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Paid</p>
                  <p className="text-2xl font-bold text-green-400 mt-1 font-heading">{stats?.paid}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} color="#4ADE80" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Overdue</p>
                  <p className="text-2xl font-bold text-red-400 mt-1 font-heading">{stats?.overdue}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Icon name="AlertCircle" size={24} color="#F87171" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Draft</p>
                  <p className="text-2xl font-bold text-slate-400 mt-1 font-heading">{stats?.draft}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center">
                  <Icon name="Edit" size={24} color="#94A3B8" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 font-body">Total Revenue</p>
                  <p className="text-2xl font-bold text-amber-400 mt-1 font-heading">₹{stats?.totalRevenue?.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Icon name="DollarSign" size={24} color="#F59E0B" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto px-6 py-6">
          {/* Filters */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Search
                </label>
                <div className="relative">
                  <Icon name="Search" size={18} color="#94A3B8" className="absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Search by invoice, customer..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e?.target?.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="all">All Status</option>
                  <option value="Paid">Paid</option>
                  <option value="Sent">Sent</option>
                  <option value="Viewed">Viewed</option>
                  <option value="Overdue">Overdue</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 font-body">
                  Customer
                </label>
                <select
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e?.target?.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="all">All Customers</option>
                  {uniqueCustomers?.map(customer => (
                    <option key={customer} value={customer}>{customer}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setCustomerFilter('all');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <Icon name="RotateCcw" size={18} />
                  <span className="ml-2">Reset Filters</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Invoices Table */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Invoice Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Issue Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Due Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider font-body">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {paginatedInvoices?.length > 0 ? (
                    paginatedInvoices?.map((invoice) => (
                      <tr key={invoice?.id} className="hover:bg-slate-700/30 transition-colors">
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-white font-body">{invoice?.invoiceNumber}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-white font-body">{invoice?.customerName}</div>
                          <div className="text-xs text-slate-400 font-caption">{invoice?.customerEmail}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-white font-body">₹{invoice?.totalAmount?.toLocaleString()}</div>
                          <div className="text-xs text-slate-400 font-caption">Tax: ₹{invoice?.taxAmount?.toLocaleString()}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-white font-body">{new Date(invoice.issueDate)?.toLocaleDateString()}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-white font-body">{new Date(invoice.dueDate)?.toLocaleDateString()}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(invoice?.status)}`}>
                            {invoice?.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setPreviewModal({ isOpen: true, invoice })}
                              className="p-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                              title="Preview Invoice"
                            >
                              <Icon name="Eye" size={16} color="#94A3B8" />
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(invoice)}
                              className="p-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 transition-colors"
                              title="Download PDF"
                            >
                              <Icon name="Download" size={16} color="#60A5FA" />
                            </button>
                            <button
                              onClick={() => handleSendEmail(invoice)}
                              className="p-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-colors"
                              title="Send Email"
                            >
                              <Icon name="Mail" size={16} color="#4ADE80" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Icon name="Inbox" size={48} color="#475569" />
                          <p className="mt-4 text-slate-400 font-body">No invoices found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                <div className="text-sm text-slate-400 font-body">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredInvoices?.length)} of {filteredInvoices?.length} results
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    className="px-3 py-1"
                  >
                    <Icon name="ChevronLeft" size={16} />
                  </Button>
                  {[...Array(totalPages)]?.map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === idx + 1
                          ? 'bg-amber-500 text-white' :'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className="px-3 py-1"
                  >
                    <Icon name="ChevronRight" size={16} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Modals */}
      {previewModal?.isOpen && (
        <InvoicePreviewModal
          invoice={previewModal?.invoice}
          onClose={() => setPreviewModal({ isOpen: false, invoice: null })}
          onDownload={handleDownloadPDF}
          onSendEmail={handleSendEmail}
        />
      )}
      {emailModal?.isOpen && (
        <EmailDeliveryModal
          invoice={emailModal?.invoice}
          onConfirm={confirmEmailSend}
          onClose={() => setEmailModal({ isOpen: false, invoice: null })}
        />
      )}
    </div>
  );
};

export default InvoiceManagementHub;